// One history guard for the whole stack. Capture popstate before BrowserRouter:
// consuming a layer must never change the route underneath it.
export function createBackNavigation(win) {
    win.__KKOMOK_BACK_NAVIGATION__ = true;
    const marker = '__pillBackGuard';
    const entries = new Map();
    let sequence = 0, guard = null, draining = false, scheduled = false;
    let pageAction = null, executing = false;
    let scrollCount = 0, previousOverflow = '';
    const top = (layersOnly = false) => [...entries.values()]
        .filter(entry => !layersOnly || entry.priority > 0)
        .sort((a, b) => b.priority - a.priority || b.order - a.order)[0];
    const samePage = () => guard && win.location.href === guard.url
        && win.history.state?.key === guard.state?.key;
    const stripMarker = state => { const next = { ...state }; delete next[marker]; return next; };
    const arm = () => {
        if (draining || executing || !entries.size) return;
        if (samePage() && win.history.state?.[marker] === guard.id) return;
        const state = stripMarker(win.history.state);
        guard = { id: `back-${++sequence}`, url: win.location.href, state };
        win.history.pushState({ ...state, [marker]: guard.id }, '', guard.url);
    };
    const reconcile = () => {
        scheduled = false;
        if (draining || executing) return;
        if (entries.size) { arm(); return; }
        if (samePage() && win.history.state?.[marker] === guard.id) {
            draining = true;
            win.history.back();
        } else guard = null;
    };
    const schedule = () => {
        if (scheduled) return;
        scheduled = true;
        // Closing one branch and opening another in the same React commit must
        // not remove and recreate history or unlock body scrolling in between.
        queueMicrotask(reconcile);
    };
    const runPageAction = action => {
        executing = true;
        Promise.resolve().then(action).catch(error => console.error('Back navigation failed:', error)).finally(() => { executing = false; schedule(); });
    };
    const leave = action => {
        if (draining && pageAction) return;
        if (draining || (samePage() && win.history.state?.[marker] === guard.id)) {
            pageAction = action;
            if (!draining) { draining = true; win.history.back(); }
            return;
        }
        return action();
    };
    const consume = (layersOnly = false) => {
        if (draining || executing) return true;
        const entry = top(layersOnly);
        if (!entry) return false;
        // A callback that refuses to close (saving) still consumes this back.
        // Keep the guard while an async form save is pending. Explicit route
        // navigation calls leave(), which removes it only when ready to leave.
        if (entry.priority === 0) runPageAction(entry.back);
        else entry.back();
        return true;
    };
    const pop = event => {
        if (draining) {
            draining = false;
            // Do not swallow an unrelated route change during cleanup.
            if (samePage()) {
                event.stopImmediatePropagation();
                guard = null;
                const action = pageAction;
                pageAction = null;
                if (action) runPageAction(action);
                else arm();
                return;
            }
            guard = null;
            pageAction = null;
            return;
        }
        if (guard && samePage() && !event.state?.[marker] && entries.size) {
            event.stopImmediatePropagation();
            // Restore before calling React: another back while saving sees the
            // same guard; the last closed layer later removes it asynchronously.
            win.history.pushState({ ...guard.state, [marker]: guard.id }, '', guard.url);
            consume();
        } else if (event.state?.[marker] && !entries.size) {
            // Forward/reload must not reopen a dismissed overlay.
            event.stopImmediatePropagation();
            guard = { id: event.state[marker], state: stripMarker(event.state), url: win.location.href };
            draining = true;
            win.history.back();
        }
    };
    const escape = event => {
        if (event.key !== 'Escape' || !top(true)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        consume(true);
    };
    const nativeBack = event => {
        event.stopImmediatePropagation();
        const consumed = consume();
        if (event.detail?.requestId) win.ReactNativeWebView?.postMessage(JSON.stringify({
            type: 'BACK_NAVIGATION_RESULT', requestId: event.detail.requestId, consumed,
        }));
    };
    // WKWebView's interactive history gesture does not reliably traverse our
    // same-document guard entries. The updated native wrapper disables its
    // competing recognizer after this handshake; older wrappers keep theirs.
    let edge = null;
    const iosEdge = win.IS_NATIVE_APP && win.NATIVE_PLATFORM === 'ios'
        && win.__KKOMOK_IOS_EDGE_BACK__ === true;
    const touchStart = event => {
        const touch = event.touches[0];
        edge = event.touches.length === 1 && touch.clientX <= 24
            ? { x: touch.clientX, y: touch.clientY, started: Date.now(), claimed: false } : null;
    };
    const touchMove = event => {
        if (!edge || event.touches.length !== 1) { edge = null; return; }
        const touch = event.touches[0], dx = touch.clientX - edge.x, dy = Math.abs(touch.clientY - edge.y);
        if (!edge.claimed && (dy > 24 || dx < -8)) { edge = null; return; }
        if (dx > 16 && dx > dy * 2) edge.claimed = true;
        if (edge.claimed) event.preventDefault();
    };
    const touchEnd = event => {
        const gesture = edge; edge = null;
        const touch = event.changedTouches[0];
        if (!gesture?.claimed || !touch || touch.clientX - gesture.x < 72
            || Math.abs(touch.clientY - gesture.y) > 60 || Date.now() - gesture.started > 1200) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const focused = win.document.activeElement;
        if (focused?.matches('input:not([type=checkbox]):not([type=radio]),textarea,[contenteditable=true]')) {
            focused.blur();
            return;
        }
        if (!consume()) win.history.back();
    };
    const touchCancel = () => { edge = null; };
    if (iosEdge) {
        win.addEventListener('touchstart', touchStart, { capture: true, passive: true });
        win.addEventListener('touchmove', touchMove, { capture: true, passive: false });
        win.addEventListener('touchend', touchEnd, { capture: true, passive: false });
        win.addEventListener('touchcancel', touchCancel, true);
        win.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'IOS_BACK_GESTURE_READY' }));
    }
    win.addEventListener('popstate', pop, true);
    win.addEventListener('keydown', escape, true);
    win.addEventListener('kkomok-native-back', nativeBack, true);
    if (win.history.state?.[marker]) {
        guard = { id: win.history.state[marker], state: stripMarker(win.history.state), url: win.location.href };
        schedule();
    }
    return {
        register(back, priority = 1201) {
            const id = ++sequence;
            entries.set(id, { back, priority, order: id });
            if (priority > 0 && scrollCount++ === 0) {
                previousOverflow = win.document.body.style.overflow;
                win.document.body.style.overflow = 'hidden';
            }
            arm();
            return () => {
                if (!entries.delete(id)) return;
                if (priority > 0 && --scrollCount === 0) win.document.body.style.overflow = previousOverflow;
                schedule();
            };
        },
        consume,
        leave,
        dispose() {
            win.removeEventListener('touchstart', touchStart, true);
            win.removeEventListener('touchmove', touchMove, true);
            win.removeEventListener('touchend', touchEnd, true);
            win.removeEventListener('touchcancel', touchCancel, true);
            win.removeEventListener('popstate', pop, true);
            win.removeEventListener('keydown', escape, true);
            win.removeEventListener('kkomok-native-back', nativeBack, true);
        },
    };
}

let instance;
export function getBackNavigation() {
    if (!instance) instance = createBackNavigation(window);
    return instance;
}
